import { useConfirm } from '@/components/Confirm';
import { Modal } from '@/components/Modal';
import {
  type CreateIntakeQuestionInput,
  type IntakeQuestion,
  type IntakeQuestionCategory,
  type IntakeQuestionKind,
  createIntakeQuestion,
  deactivateIntakeQuestion,
  listIntakeQuestions,
  reorderIntakeQuestions,
  updateIntakeQuestion,
} from '@/lib/api';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const KINDS: IntakeQuestionKind[] = ['boolean', 'acknowledgement', 'select', 'text', 'number'];
const CATEGORIES: IntakeQuestionCategory[] = ['boarding', 'daycare', 'grooming'];

const CATEGORY_LABEL: Record<IntakeQuestionCategory, string> = {
  boarding: 'Boarding',
  daycare: 'Daycare',
  grooming: 'Grooming',
};

function parseCategories(json: string): IntakeQuestionCategory[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((c): c is IntakeQuestionCategory =>
      (CATEGORIES as string[]).includes(c as string),
    );
  } catch {
    return [];
  }
}

/**
 * Sub-spec 06 Plan 3 — tenant-editable intake question settings.
 *
 * Grouped per service category (boarding / daycare / grooming); a question
 * can appear in multiple categories. Each row offers reorder, edit, and
 * deactivate.
 */
export function IntakeQuestionsPage() {
  const [questions, setQuestions] = useState<IntakeQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<IntakeQuestion | null>(null);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listIntakeQuestions();
      setQuestions(res.questions.filter((q) => q.isActive === 1));
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDeactivate(id: string) {
    const yes = await confirm({
      title: 'Deactivate this question?',
      message: 'Historical answers stay attached to past bookings for audit.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await deactivateIntakeQuestion(id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleReorder(index: number, direction: 'up' | 'down') {
    const sorted = [...questions].sort((a, b) => a.position - b.position);
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= sorted.length) return;
    const a = sorted[index];
    const b = sorted[swapWith];
    if (!a || !b) return;
    try {
      await reorderIntakeQuestions([
        { id: a.id, position: b.position },
        { id: b.id, position: a.position },
      ]);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const sorted = [...questions].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <h1 className="font-heading text-2xl font-medium tracking-tight">Intake questions</h1>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <p className="mb-6 text-sm text-muted-foreground">
        Questions surfaced on the check-in kiosk for each booking type. Required questions block
        check-in until answered. Deactivating a question removes it from future check-ins;
        historical answers remain attached to past bookings.
      </p>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press"
        >
          <Plus className="size-3.5" strokeWidth={1.75} />
          New question
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : sorted.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface px-5 py-12 text-center">
          <p className="text-sm text-muted-foreground">No intake questions yet.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {sorted.map((q, i) => {
            const cats = parseCategories(q.appliesToCategories);
            return (
              <li
                key={q.id}
                className="flex items-center gap-3 px-5 py-3"
                data-testid={`question-row-${q.slug}`}
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => void handleReorder(i, 'up')}
                    disabled={i === 0}
                    className="text-muted-foreground disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp className="size-3.5" strokeWidth={1.75} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleReorder(i, 'down')}
                    disabled={i === sorted.length - 1}
                    className="text-muted-foreground disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown className="size-3.5" strokeWidth={1.75} />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium">{q.label}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    <span className="font-mono">{q.slug}</span>
                    {' · '}
                    {q.kind}
                    {q.required === 1 && ' · required'}
                    {' · '}
                    {cats.map((c) => CATEGORY_LABEL[c]).join(', ') || 'no categories'}
                    {q.appliesWhenMultiPet === 1 && ' · multi-pet only'}
                    {q.appliesWhenDogPresent === 1 && ' · dog only'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(q);
                    setEditorOpen(true);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label="Edit"
                >
                  <Pencil className="size-3.5" strokeWidth={1.75} />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeactivate(q.id)}
                  className="text-destructive hover:text-destructive/80"
                  aria-label="Deactivate"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {editorOpen && (
        <QuestionEditor
          initial={editing}
          existingPositions={sorted.map((q) => q.position)}
          onClose={() => {
            setEditorOpen(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setEditorOpen(false);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

interface EditorProps {
  initial: IntakeQuestion | null;
  existingPositions: number[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function QuestionEditor({ initial, existingPositions, onClose, onSaved }: EditorProps) {
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [kind, setKind] = useState<IntakeQuestionKind>(
    (initial?.kind as IntakeQuestionKind) ?? 'boolean',
  );
  const [optionsText, setOptionsText] = useState(() => {
    if (!initial?.optionsJson) return '';
    try {
      const parsed = JSON.parse(initial.optionsJson);
      return Array.isArray(parsed) ? parsed.join('\n') : '';
    } catch {
      return '';
    }
  });
  const [required, setRequired] = useState(initial?.required === 1);
  const [isAck, setIsAck] = useState(initial?.isAcknowledgement === 1);
  const [cats, setCats] = useState<Record<IntakeQuestionCategory, boolean>>(() => {
    const parsed = initial ? parseCategories(initial.appliesToCategories) : ['boarding'];
    return {
      boarding: parsed.includes('boarding'),
      daycare: parsed.includes('daycare'),
      grooming: parsed.includes('grooming'),
    };
  });
  const [multiPet, setMultiPet] = useState(initial?.appliesWhenMultiPet === 1);
  const [dogOnly, setDogOnly] = useState(initial?.appliesWhenDogPresent === 1);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setEditError(null);
    try {
      const selectedCats = (Object.keys(cats) as IntakeQuestionCategory[]).filter((k) => cats[k]);
      if (selectedCats.length === 0) {
        setEditError('Pick at least one service category.');
        setSaving(false);
        return;
      }
      const options =
        kind === 'select'
          ? optionsText
              .split('\n')
              .map((o) => o.trim())
              .filter((o) => o.length > 0)
          : undefined;
      if (kind === 'select' && (!options || options.length === 0)) {
        setEditError('Add at least one option (one per line) for select questions.');
        setSaving(false);
        return;
      }

      if (initial) {
        await updateIntakeQuestion(initial.id, {
          label,
          kind,
          ...(kind === 'select' && options ? { options } : { options: null }),
          required,
          isAcknowledgement: isAck,
          appliesToCategories: selectedCats,
          appliesWhenMultiPet: multiPet,
          appliesWhenDogPresent: dogOnly,
        });
      } else {
        const maxPos = existingPositions.length === 0 ? 0 : Math.max(...existingPositions) + 1;
        const body: CreateIntakeQuestionInput = {
          label,
          kind,
          required,
          isAcknowledgement: isAck,
          appliesToCategories: selectedCats,
          appliesWhenMultiPet: multiPet,
          appliesWhenDogPresent: dogOnly,
          position: maxPos,
        };
        if (slug.trim().length > 0) body.slug = slug.trim();
        if (kind === 'select' && options) body.options = options;
        await createIntakeQuestion(body);
      }
      await onSaved();
    } catch (e) {
      setEditError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <div className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold">{initial ? 'Edit question' : 'New question'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {editError && (
          <div className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {editError}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor="iq-label" className="mb-1 block text-xs text-muted-foreground">
              Label
            </label>
            <input
              id="iq-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            />
          </div>
          {!initial && (
            <div>
              <label htmlFor="iq-slug" className="mb-1 block text-xs text-muted-foreground">
                Slug (auto-generated if blank)
              </label>
              <input
                id="iq-slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="lowercase_with_underscores"
                className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono"
              />
            </div>
          )}
          <div>
            <label htmlFor="iq-kind" className="mb-1 block text-xs text-muted-foreground">
              Kind
            </label>
            <select
              id="iq-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as IntakeQuestionKind)}
              className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          {kind === 'select' && (
            <div>
              <label htmlFor="iq-options" className="mb-1 block text-xs text-muted-foreground">
                Options (one per line)
              </label>
              <textarea
                id="iq-options"
                rows={4}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-1.5 text-sm"
              />
            </div>
          )}
          {kind === 'text' && (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Preview</p>
              <input
                type="text"
                disabled
                placeholder="Free-text answer…"
                className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground cursor-not-allowed"
              />
            </div>
          )}
          {kind === 'number' && (
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Preview</p>
              <input
                type="number"
                disabled
                placeholder="0"
                className="w-full rounded-md border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground cursor-not-allowed"
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {CATEGORIES.map((c) => (
              <label key={c} className="flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={cats[c]}
                  onChange={(e) => setCats({ ...cats, [c]: e.target.checked })}
                />
                {CATEGORY_LABEL[c]}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
            />
            Required (blocks check-in until answered)
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={isAck} onChange={(e) => setIsAck(e.target.checked)} />
            Acknowledgement (renders as "I confirm" instead of yes/no)
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={multiPet}
              onChange={(e) => setMultiPet(e.target.checked)}
            />
            Only show when 2+ pets
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={dogOnly}
              onChange={(e) => setDogOnly(e.target.checked)}
            />
            Only show when at least one dog present
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || label.trim().length === 0}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
